/**
 * Incident Editor Dialog - Privacy & Compliance Tab (PROJ-007)
 * Privacy flags: isPrivacy, isBreach, needsDpia, involvesMinors, involvesHealth
 */

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Shield } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';
import type { IncidentFormData } from './types';

interface PrivacyTabProps {
  form: UseFormReturn<IncidentFormData>;
  isViewMode?: boolean;
}

export function PrivacyTab({ form, isViewMode = false }: PrivacyTabProps) {
  return (
    <div className="space-y-4">
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertDescription>
          Configuración de privacidad y cumplimiento normativo
        </AlertDescription>
      </Alert>

      <FormField
        control={form.control}
        name="isPrivacy"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isViewMode}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                Incidente de Privacidad
              </FormLabel>
              <p className="text-sm text-muted-foreground">
                Marca si es un incidente relacionado con datos personales
              </p>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="isBreach"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isViewMode}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                Brecha de Datos
              </FormLabel>
              <p className="text-sm text-muted-foreground">
                Marca si hay evidencia de exposición de datos
              </p>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="needsDpia"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isViewMode}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                Requiere DPIA
              </FormLabel>
              <p className="text-sm text-muted-foreground">
                Evaluación de Impacto en Protección de Datos
              </p>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="involvesMinors"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isViewMode}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                Involucra Menores
              </FormLabel>
              <p className="text-sm text-muted-foreground">
                Datos de personas menores de edad
              </p>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="involvesHealth"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isViewMode}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                Involucra Datos de Salud
              </FormLabel>
              <p className="text-sm text-muted-foreground">
                Información médica o de salud
              </p>
            </div>
          </FormItem>
        )}
      />
    </div>
  );
}
